/**
 * @name ConfigModule
 * @description Module used for retrieving company users.
 * @author Kelvin John Santos
 * @version 1.0
 * @since August 22, 2019
 */

import get from "lodash/get";
import { AppFeature } from "@sleek-sdk/sleek-cms";

// Utilities
import LocalStoreManager from "@/utilities/local-store-manager";
import getAppCustomEnv from "@/utilities/get-app-custom-env";
import getPlatformConfig from "@/utilities/get-platform-config";
import { getAppFeatureProp } from "@/utilities/app-feature-util";
import GetPlatformConfigProxy from "@/proxies/back-end/config/get-platform-config";


// Constants
import { Constants } from "customer-sdk/constants";

const { SLEEK_APP_CACHE_KEY } = Constants.STORE_CONSTANTS;
const { SET_PLATFORM_CONFIG, CLEAR_PLATFORM_CONFIG, SET_IS_LOADING } = Constants.CONFIG_CONSTANTS;

const sleekApp = JSON.parse(localStorage.getItem(SLEEK_APP_CACHE_KEY) || "{}");

// initial state
function initialState() {
  return {
    platformConfig: get(sleekApp, "configModule.platformConfig", {}),
    authenticatedPlatformConfig: null,
    isLoading: false,
    IS_BILLING_PROFILE: true,
    isLogin: false,
    phoneCodeList: [],
    phoneCode: "",
    country: "",
  };
}

const state = initialState;

// getters
const getters = {
  platformConfig: (state) => state.platformConfig,
  getIsLoading: (state) => state.isLoading,
  isBillingProfile: (state) => state.IS_BILLING_PROFILE,
  getIsLogin: (state) => state.isLogin,
  getPhoneCodeList: (state) => state.phoneCodeList,
};

// actions
const actions = {
  async getAppFeature({ state, dispatch }, name) {
    if (!state.platformConfig.cmsAppFeatures) {
      const platformConfig = await dispatch("GET_PLATFORM_CONFIG");
      await dispatch("SET_PLATFORM_CONFIG", platformConfig);
    }

    const feature = getAppFeatureProp(state.platformConfig.cmsAppFeatures, name);
    return new AppFeature(feature);
  },
  async getAuthenticatedAppFeature({ state, dispatch }, name) {
    if (!state.authenticatedPlatformConfig) {
      await dispatch("loadAuthenticatedFeatures");
    }

    const feature = getAppFeatureProp(state.authenticatedPlatformConfig, name);
    return new AppFeature(feature);
  },
  async getGeneralAppFeature({ state, dispatch }, name) {
    if (!state.platformConfig.cmsGeneralFeatureList) {
      const platformConfig = await dispatch("GET_PLATFORM_CONFIG");
      await dispatch("SET_PLATFORM_CONFIG", platformConfig);
    }

    const feature = getAppFeatureProp(state.platformConfig.cmsGeneralFeatureList, name);
    return new AppFeature(feature);
  },
  async loadAuthenticatedFeatures({ state }) {
    const proxy = new GetPlatformConfigProxy();
    state.authenticatedPlatformConfig = await proxy.getAuthenticatedAppFeatures();
  },
  // eslint-disable-next-line no-unused-vars
  async GET_PLATFORM_CONFIG({ state, commit }, payload) {
    return await getPlatformConfig(window.location.hostname);
  },
  SET_PLATFORM_CONFIG({ commit }, payload) {
    console.log("SET_PLATFORM_CONFIG>>>>> INSIDE COMMON");
    getAppCustomEnv().then((appCustomEnv) => {
      const localStore = new LocalStoreManager();
      localStore.setLastKnownBuildVersion(appCustomEnv.VUE_APP_BUILD_NUMBER);
    });
    commit(SET_PLATFORM_CONFIG, payload);
  },
  CLEAR_PLATFORM_CONFIG({ commit }, payload) {
    let store = new LocalStoreManager();
    store.setLastKnownBuildVersion(null);
    commit(CLEAR_PLATFORM_CONFIG, payload);
  },
  SET_IS_LOADING({ commit }, payload) {
    commit(SET_IS_LOADING, payload);
  },
  enableOTPListerner({ commit }, payload) {
    if (payload) {
      commit("userModule/setUser", payload.user, { root: true });
      commit("userModule/setUserFormData", payload.userFormData, {
        root: true,
      });
    }
  },
  resendOTP() {},
};

// mutations
const mutations = {
  SET_PLATFORM_CONFIG(state, payload) {
    state.platformConfig = payload;
  },
  // eslint-disable-next-line no-unused-vars
  CLEAR_PLATFORM_CONFIG(state, payload) {
    state.platformConfig = {};
    state.authenticatedPlatformConfig = null;
  },
  SET_IS_LOADING(state, payload) {
    state.isLoading = payload;
  },
  SET_IS_BILLING_PROFILE(state, payload) {
    state.IS_BILLING_PROFILE = payload;
  },
  setUserData(state, payload) {
    state.userData = payload;
  },
  setIsLogin(state, payload) {
    state.isLogin = payload;
  },
  setPhoneData(state, payload) {
    if (payload) {
      state.phoneCodeList = payload.phoneCodeList;
      state.phoneCode = payload.phoneCode;
      state.country = payload.country;
      let obj = {
        phoneCodeList: state.phoneCodeList,
        phoneCode: state.phoneCode,
        country: state.country,
      };
      localStorage.setItem("phoneData", JSON.stringify(obj));
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
